import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  NgZone,
  OnDestroy,
  output,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import data from '@emoji-mart/data/sets/15/google.json';
import { Picker, SearchIndex } from 'emoji-mart';

import { ThemeService } from '@spaces-ui/theme';

import { MdwEmoji, icons, USE_NATIVE_EMOJI, EMOJI_SET } from '../../models';
import { MdwEmojiSearchService } from '../mdw-emoji-search-service';

// emoji-mart mutates the shared `data.emojis` dict on every Picker init,
// merging each org's custom emojis into it. Snapshot the pristine native IDs
// at module load so `getStaleCustomEmojiIds` can reliably tell native apart
// from "custom left behind by a previous org".
const NATIVE_EMOJI_IDS: ReadonlySet<string> = new Set(
  Object.keys((data as { emojis: Record<string, unknown> }).emojis),
);

/** Shape of the emoji-mart v5 onEmojiSelect callback payload. */
interface EmojiMartSelection {
  id: string;
  name: string;
  native?: string;
  unified?: string;
  src?: string;
  shortcodes?: string;
  keywords?: string[];
  custom?: boolean;
}

@Component({
  selector: 'mdw-emoji-panel',
  templateUrl: './mdw-emoji-panel.html',
  styleUrls: ['./mdw-emoji-panel.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    '[class.native-emojis]': 'isNative',
  },
})
export class MdwEmojiPanel implements OnDestroy {
  private zone = inject(NgZone);
  private themeService = inject(ThemeService);
  private emojiSearchService = inject(MdwEmojiSearchService);

  public customEmojis = input<unknown[]>([]);
  public emojiSelect = output<MdwEmoji>();

  protected readonly isNative = USE_NATIVE_EMOJI;
  protected readonly emojiSet = EMOJI_SET;

  public pickerContainer = viewChild<ElementRef<HTMLDivElement>>('pickerContainer');

  private picker: InstanceType<typeof Picker> | null = null;
  private titleObserver: MutationObserver | null = null;
  private isInitialized = false;
  private skinToneTarget: ShadowRoot | null = null;

  /**
   * Bound so add/removeEventListener see the same reference.
   *
   * Deliberately NOT wrapped in `zone.run`: these callers fire on every picker hover,
   * click, and keystroke — all outside the zone, by design of `runOutsideAngular` in
   * createPicker. `scheduleSkinToneRefresh` coalesces the burst and enters the zone
   * itself only when the stored tone actually changed, so the OnPush hover bar still
   * repaints on a real selection without every interaction ticking the app.
   */
  private readonly refreshSkinTone = () => this.emojiSearchService.scheduleSkinToneRefresh();

  protected pickerTheme = computed(() => (this.themeService.isDark() ? 'dark' : 'light'));

  /** Convert custom emojis to emoji-mart v5 category format. */
  protected customEmojisV5 = computed(() => {
    const emojis = this.customEmojis();

    if (!emojis.length) {
      return undefined;
    }

    return [
      {
        id: 'custom',
        name: 'Custom',
        emojis: emojis.map(e => {
          const emoji = e as { name: string; imageUrl: string; keywords?: string[] };

          return {
            id: emoji.name,
            name: emoji.name,
            skins: [{ src: emoji.imageUrl }],
            keywords: emoji.keywords ?? [],
          };
        }),
      },
    ];
  });

  /** Category icon overrides — adapted to emoji-mart v5 format. */
  private categoryIcons = this.buildCategoryIcons();

  public constructor() {
    // Recreate picker when custom emojis change
    effect(() => {
      const custom = this.customEmojisV5();
      const container = this.pickerContainer();
      const theme = this.pickerTheme();

      if (container) {
        const currentCustomIds = new Set(
          custom?.flatMap(category => category.emojis.map(e => e.id)) ?? [],
        );
        const exceptEmojis = this.getStaleCustomEmojiIds(currentCustomIds);
        this.pruneStaleCustomEmojisFromData(currentCustomIds);
        this.createPicker(container.nativeElement, custom, theme, exceptEmojis);
      }
    });
  }

  /**
   * emoji-mart's `SearchIndex` iterates the shared `data.emojis` dict, which
   * accumulates custom emojis from every org the Picker has been initialized
   * with. The `exceptEmojis` prop only filters category rendering, not search —
   * so stale customs from previous orgs still surface when the user types in
   * the search box. Delete them from `data.emojis` and reset the search pool
   * so the next search rebuilds from a clean dict.
   */
  private pruneStaleCustomEmojisFromData(currentCustomIds: Set<string>): void {
    const emojis = (data as { emojis: Record<string, unknown> }).emojis;

    for (const id of Object.keys(emojis)) {
      if (!NATIVE_EMOJI_IDS.has(id) && !currentCustomIds.has(id)) {
        delete emojis[id];
      }
    }

    SearchIndex.reset();
  }

  /**
   * emoji-mart persists frequently-used emoji IDs in browser localStorage under
   * `emoji-mart.frequently`. The store is not org-scoped, so custom emojis picked
   * in one org linger in another org's Frequently Used section even though they
   * don't exist there. Compute the stale IDs so we can feed them to the picker's
   * `exceptEmojis` prop for filtering.
   */
  private getStaleCustomEmojiIds(currentCustomIds: Set<string>): string[] {
    const raw = localStorage.getItem('emoji-mart.frequently');
    if (!raw) {
      return [];
    }

    try {
      const frequent = JSON.parse(raw) as Record<string, number>;

      return Object.keys(frequent).filter(
        id => !NATIVE_EMOJI_IDS.has(id) && !currentCustomIds.has(id),
      );
    } catch {
      return [];
    }
  }

  public ngOnDestroy(): void {
    this.titleObserver?.disconnect();
    this.titleObserver = null;
    this.detachSkinToneListeners();
    this.destroyPicker();
  }

  public handleEmojiSelect(selection: EmojiMartSelection) {
    const emoji: MdwEmoji = {
      id: selection.id,
      name: selection.name,
      native: selection.native ?? '',
      unified: selection.unified?.toUpperCase() ?? '',
      colons: selection.shortcodes ?? `:${selection.id}:`,
      imageUrl: selection.src,
      custom: selection.custom,
    };

    this.zone.run(() => this.emojiSelect.emit(emoji));
  }

  public setFocus() {
    const container = this.pickerContainer()?.nativeElement;
    if (!container) {
      return;
    }

    const pickerEl = container.querySelector('em-emoji-picker');
    const shadowRoot = pickerEl?.shadowRoot;

    if (shadowRoot) {
      const searchInput = shadowRoot.querySelector('input[type="search"]') as HTMLInputElement;
      searchInput?.focus();
    }
  }

  public initEmojiPanel() {
    if (this.isInitialized) {
      return;
    }

    this.isInitialized = true;
  }

  private createPicker(
    container: HTMLDivElement,
    custom: unknown[] | undefined,
    theme: 'dark' | 'light',
    exceptEmojis: string[],
  ) {
    this.destroyPicker();

    this.zone.runOutsideAngular(() => {
      this.picker = new Picker({
        data,
        set: this.isNative ? 'native' : this.emojiSet,
        custom,
        exceptEmojis,
        onEmojiSelect: (emoji: EmojiMartSelection) => this.handleEmojiSelect(emoji),
        autoFocus: true,
        categoryIcons: this.categoryIcons,
        theme,
        previewPosition: 'bottom',
      });

      container.innerHTML = '';
      container.appendChild(this.picker as unknown as Node);

      // Inject CSS into the shadow root to move nav from horizontal top to vertical left sidebar
      this.injectNavOverrides();
    });
  }

  private destroyPicker() {
    const container = this.pickerContainer()?.nativeElement;

    if (container) {
      container.innerHTML = '';
    }

    this.picker = null;
  }

  /**
   * Injects a <style> into the picker's open shadow root to
   * rearrange the category nav from a horizontal top bar into
   * a vertical sidebar on the left — matching the old layout.
   */
  private injectNavOverrides() {
    const pickerEl = this.pickerContainer()?.nativeElement.querySelector('em-emoji-picker');
    const shadowRoot = pickerEl?.shadowRoot;

    if (!shadowRoot) {
      return;
    }

    const style = document.createElement('style');
    style.textContent = `
      /* ── Design system theme overrides ── */
      /* CSS custom properties from the host document (--color-*) inherit into shadow DOM */
      #root {
        position: relative;
        background-color: var(--color-surface-elevated) !important;
        color: var(--color-text-primary) !important;
        border-color: var(--color-border-primary) !important;
        /* sidebar-width feeds into the width calc: lineWidth + padding + sidebar-width.
           We need room for the 44px left nav + 6px scrollbar. */
        --sidebar-width: 56px;
        --padding: 4px;
      }

      #root .spacer {
        height: 6px;
      }

      .search input {
        background-color: var(--color-input-surface) !important;
        border: 1px solid var(--color-border-primary) !important;
        border-radius: 8px;
        color: var(--color-text-primary) !important;
        font-family: Inter, sans-serif;
        font-size: 14px;
        height: 32px;
        transition: background-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
      }

      .search input:hover {
        border-color: var(--color-border-strong) !important;
        background-color: var(--color-input-surface-hover) !important;
      }

      .search input:focus {
        border-color: var(--color-border-focus) !important;
        box-shadow: 0 0 0 4px var(--color-input-focus-ring) !important;
      }

      .search input::placeholder {
        color: var(--color-text-secondary);
      }

      .sticky {
        background-color: var(--color-surface-elevated) !important;
        font-size: 12px;
        font-weight: 600;
        font-family: Inter, sans-serif;
        padding-top: 8px;
        padding-bottom: 6px;
      }

      .category:not(:first-of-type) .sticky {
        padding-top: 24px;
      }

      .category .label {
        background-color: var(--color-surface-elevated) !important;
        color: var(--color-text-primary) !important;
        font-family: Inter, sans-serif;
      }

      #preview {
        background-color: var(--color-surface-muted);
        padding-right: 16px;
      }

      /* Hide the idle "Pick an emoji..." placeholder and its default emoji,
         but keep the skin tone selector visible */
      #preview .preview-placeholder,
      #preview:has(.preview-placeholder) > .flex:first-child {
        visibility: hidden;
      }

      /* Emoji button round hover — use meadow surface color */
      .category button .background {
        border-radius: 8px;
      }

      .category button:hover .background,
      .category button[aria-selected] .background {
        background-color: var(--color-surface-muted) !important;
      }

      /* ── Vertical left-nav layout ── */
      #nav {
        position: absolute;
        top: 37px;
        left: 0;
        z-index: 2;
        height: 377px;
        width: 40px;
        padding: 4px;
        overflow: hidden;
      }

      #nav > .flex {
        flex-direction: column;
        height: 100%;
        justify-content: space-evenly;
        gap: 0;
      }

      #nav > .flex > .bar {
        display: none;
      }

      #nav button {
        width: 32px;
        height: 32px;
        max-height: 32px;
        padding: 4px;
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--color-text-primary) !important;
        opacity: 0.8;
        transition: background-color 0.2s, opacity 0.2s;
      }

      #nav button:hover {
        background-color: var(--color-surface-muted);
      }

      #nav button[aria-selected="true"] {
        opacity: 1;
        background-color: var(--color-surface-muted) !important;
      }

      .scroll {
        padding-left: 44px !important;
      }

      /* ── Scrollbar styling ── */
      .scroll {
        padding-right: 2px !important;
      }

      .scroll::-webkit-scrollbar {
        width: 6px !important;
        height: 6px !important;
      }

      .scroll::-webkit-scrollbar-track {
        background: transparent !important;
        margin: 4px;
        border-radius: 20px;
      }

      .scroll::-webkit-scrollbar-thumb {
        border: none !important;
        border-radius: 10px !important;
        background: transparent !important;
      }

      .scroll:hover::-webkit-scrollbar-thumb {
        background: var(--scrollbar-thumb, rgba(0,0,0,0.2)) !important;
      }

      .scroll::-webkit-scrollbar-corner {
        background: transparent !important;
      }

      /* ── Skin tone tooltip (styled to match matTooltip) ── */
      .skin-tone-button {
        position: relative;
      }

      .skin-tone-button::after {
        content: 'Choose skin tone';
        position: absolute;
        bottom: calc(100% + 8px);
        right: -22px;
        background: var(--color-surface-flat-inverse);
        color: var(--color-text-inverse);
        font-family: Inter, sans-serif;
        font-feature-settings:
          'ss03' on,
          'cv05' on;
        font-size: 13px;
        height: 34px;
        padding: 4px 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 6px;
        white-space: nowrap;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.15s;
      }

      .skin-tone-button:hover::after {
        opacity: 1;
      }

      /* ── Skin tone picker dropdown ── */
      .menu {
        background-color: var(--color-surface-elevated) !important;
        border: none;
        box-shadow:
          var(--shadow-100-layer1-x) var(--shadow-100-layer1-y) var(--shadow-100-layer1-blur) var(--shadow-100-layer1-spread) var(--shadow-100-layer1-color),
          var(--shadow-100-layer2-x) var(--shadow-100-layer2-y) var(--shadow-100-layer2-blur) var(--shadow-100-layer2-spread) var(--shadow-100-layer2-color),
          var(--shadow-100-layer3-x) var(--shadow-100-layer3-y) var(--shadow-100-layer3-blur) var(--shadow-100-layer3-spread) var(--shadow-100-layer3-color),
          var(--shadow-100-layer4-x) var(--shadow-100-layer4-y) var(--shadow-100-layer4-blur) var(--shadow-100-layer4-spread) var(--shadow-100-layer4-color) !important;
      }

      .menu .option {
        color: var(--color-text-primary) !important;
      }

      .menu input[type="radio"] + .option:hover {
        background-color: var(--color-button-overlay-tertiary-hover);
      }

      .menu input[type="radio"]:checked + .option {
        box-shadow:
          var(--tertiary-focus-ring-layer1-x) var(--tertiary-focus-ring-layer1-y) var(--tertiary-focus-ring-layer1-blur) var(--tertiary-focus-ring-layer1-spread) var(--tertiary-focus-ring-layer1-color);
      }
    `;
    shadowRoot.appendChild(style);

    // Strip native title tooltips from nav buttons and skin tone selector.
    // emoji-mart re-renders elements (e.g. skin tone button on preview updates),
    // so keep the observer alive for the component's lifetime.
    const stripTitles = () => {
      shadowRoot
        .querySelectorAll('#nav button[title]')
        .forEach(btn => btn.removeAttribute('title'));
      shadowRoot.querySelector('.skin-tone-button[title]')?.removeAttribute('title');
    };

    stripTitles();

    this.titleObserver?.disconnect();
    this.titleObserver = new MutationObserver(() => {
      stripTitles();

      // THIS is what keeps the cached skin tone correct — verified by hand in the running
      // app, where removing this line makes the tone lag one selection behind again.
      //
      // Picking a tone makes emoji-mart re-render its skin-tone button, and that render
      // necessarily happens *after* its `Store.set`, so this path cannot observe a stale
      // value. The click/keydown listeners below were an earlier attempt at the same job
      // and are NOT sufficient on their own, despite reading on a deferred microtask.
      // Don't rely on them; don't remove this in favour of them.
      //
      // The refresh coalesces into a single microtask, so the frequent preview-hover
      // mutations cost one guarded storage read per task at most.
      this.refreshSkinTone();
    });
    this.titleObserver.observe(shadowRoot, {
      childList: true,
      subtree: true,
      attributeFilter: ['title'],
    });

    // The skin tone selector lives in here, and emoji-mart persists the choice itself
    // without exposing a change callback. Refresh our cached tone off any interaction
    // with the picker rather than depending on its internal markup: the listeners are
    // untargeted, so they survive emoji-mart restructuring its shadow DOM.
    //
    // `click` and `keydown` are the two events that reach emoji-mart's handleSkinClick
    // (the swatch button's onClick, and Enter/Space/Tab on the radio) — its radio
    // `onChange` only drives the hover preview and writes nothing. Capture phase is
    // required because emoji-mart's outside-click handler calls
    // stopImmediatePropagation(); see scheduleSkinToneRefresh for why the read is
    // deferred rather than taken here.
    if (this.skinToneTarget !== shadowRoot) {
      this.detachSkinToneListeners();
      shadowRoot.addEventListener('click', this.refreshSkinTone, true);
      shadowRoot.addEventListener('keydown', this.refreshSkinTone, true);
      this.skinToneTarget = shadowRoot;
    }
  }

  private detachSkinToneListeners() {
    this.skinToneTarget?.removeEventListener('click', this.refreshSkinTone, true);
    this.skinToneTarget?.removeEventListener('keydown', this.refreshSkinTone, true);
    this.skinToneTarget = null;
  }

  private buildCategoryIcons(): Record<string, { svg: string }> {
    const result: Record<string, { svg: string }> = {};

    for (const [key, pathData] of Object.entries(icons)) {
      result[key] = {
        svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="${pathData}" fill="currentColor" fill-rule="evenodd"/></svg>`,
      };
    }

    return result;
  }
}
